/**
 * Sanitize user input to prevent XSS attacks
 * For display purposes - React already escapes JSX expressions,
 * but this provides extra protection for edge cases
 */

/**
 * Sanitize HTML for display in dangerouslySetInnerHTML contexts
 * Converts HTML entities to prevent XSS
 * Note: For normal React JSX, React already escapes expressions. Use sanitizeText() instead.
 */
export const sanitizeHtml = (text) => {
  if (!text || typeof text !== "string") return "";

  return text
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#x27;")
    .replaceAll("/", "&#x2F;");
};

/**
 * Normalize text for React JSX display
 * NOTE: React automatically escapes JSX expressions to prevent XSS
 * This function only removes control characters for clean display
 * Use sanitizeHtml() if you need to use dangerouslySetInnerHTML
 *
 * IMPORTANT SECURITY NOTE:
 * - This does NOT sanitize HTML - React's JSX auto-escaping handles that
 * - Message content from database is safe to display in JSX: {normalizeText(content)}
 * - Backend stores messages raw; frontend relies on React escaping for XSS protection
 * - NEVER use dangerouslySetInnerHTML with user content without sanitizeHtml()
 */
export const normalizeText = (text) => {
  if (!text || typeof text !== "string") return "";

  // Remove control characters and normalize whitespace
  return text.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "").trim();
};

// Alias for backwards compatibility and semantic clarity
export const sanitizeText = normalizeText;

/**
 * Sanitize text for use in HTML attributes
 */
export const sanitizeAttribute = (text) => {
  if (!text || typeof text !== "string") return "";

  return text
    .replace(/[^\w\s-]/g, "") // Only allow word chars, spaces, and hyphens
    .trim();
};

/**
 * Truncate text to a maximum length
 */
export const truncate = (text, maxLength = 100) => {
  if (!text || typeof text !== "string") return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

/**
 * Sanitize widget name for display
 * Allows alphanumeric (including Unicode letters/numbers), spaces, and common punctuation
 * Supports international characters (é, ñ, 中文, etc.)
 */
export const sanitizeWidgetName = (name) => {
  if (!name || typeof name !== "string") return "Untitled Widget";

  // Unicode-aware regex:
  // \p{L} = any Unicode letter (a-z, А-Я, 中, etc.)
  // \p{N} = any Unicode number (0-9, ०-९, etc.)
  // \s = whitespace
  // Other characters: hyphens, underscores, parentheses
  const sanitized = name.replace(/[^\p{L}\p{N}\s\-_()]/gu, "").trim();

  return sanitized || "Untitled Widget";
};

/**
 * Format date in a safe way
 */
export const formatDate = (dateString) => {
  if (!dateString) return "Never";

  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (Number.isNaN(date.getTime())) return "Invalid date";

    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins === 1) return "1 minute ago";
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours === 1) return "1 hour ago";
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return "1 day ago";
    if (diffDays < 7) return `${diffDays} days ago`;

    // For older dates, show formatted date
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    return "Invalid date";
  }
};

/**
 * Create safe confirmation message for delete operations
 * Prevents script injection in window.confirm()
 */
export const createSafeConfirmMessage = (itemName, itemType = "item") => {
  const safeName = sanitizeWidgetName(itemName);
  return `Are you sure you want to delete this ${itemType}? "${safeName}"`;
};

// NOTE: sanitizeUrl has been consolidated into formatters.js which includes SSRF protection.
// Import from './formatters' for URL sanitization: import { sanitizeUrl } from './formatters';

const sanitizeUtils = {
  normalizeText,
  sanitizeText, // Alias for normalizeText
  sanitizeHtml,
  sanitizeAttribute,
  truncate,
  sanitizeWidgetName,
  formatDate,
  createSafeConfirmMessage,
};

export default sanitizeUtils;
